// src/hooks/usePWAInstall.ts
import { useEffect, useState } from "react";
import { useNavigationStore } from "../stores/navigation";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const { resetAllStoreNavItemsState, setIsAddToHomeHeaderVisible } = useNavigationStore();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleInstalled = () => {
      console.log("PWA was installed!");
      // Your one-time setup logic here (e.g., tracking, welcome message)
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    // Cleanup the event listener
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt),
        window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const promptToInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // Reset the deferred prompt after use
    setDeferredPrompt(null);
    setIsInstallable(outcome !== "accepted");

    if (outcome === "accepted") {
      resetAllStoreNavItemsState();
      setIsAddToHomeHeaderVisible(false);
    }
  };

  return { isInstallable, promptToInstall };
};
